import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./AdvertisementTable.module.scss";
import SwitchText from "../../common/hookForm/SwitchText";
import EmptyIcon from "../../../assets/images/svg/empty.svg";
import DeleteAdvertisementModal, {
  DeleteAdvertisementModalRef,
} from "../DeleteAdvertisementModal";
import { CreateAdvertisementModalRef } from "../CreateAdvertisementModal";
import { useAdvertisementsStore } from "../../../store/advertisements";
import { useAlertStore } from "../../../store/alertStore";
import { IAdvertisement } from "../../../setup/api/services/advertisements/models";
import { getDate } from "../../../setup/util/dateFormatter";
import { IApiResponse } from "../../../setup/api/models";

// Sortable Row Component
interface SortableRowProps {
  advertisement: IAdvertisement;
  children: React.ReactNode;
}

const SortableRow: React.FC<SortableRowProps> = ({
  advertisement,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: advertisement.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.table_row}>
      <div
        className={`${styles.table_cell} ${styles.drag_handle}`}
        style={{ width: "10%", textAlign: "right", cursor: "grab" }}
        {...attributes}
        {...listeners}>
        <img src="/assets/images/svg/ad_menu.svg" alt="menu" />
      </div>
      {children}
    </div>
  );
};

interface AdvertisementTableProps {
  modalRef: React.RefObject<CreateAdvertisementModalRef>;
  onOrderChange?: (hasPendingOrder: boolean, isSaving: boolean) => void;
  onSaveOrderRequest?: () => void;
}

export interface AdvertisementTableRef {
  saveOrder: () => Promise<void>;
  cancelReorder: () => void;
  hasPendingOrder: () => boolean;
  isSaving: () => boolean;
}

const AdvertisementTable = forwardRef<
  AdvertisementTableRef,
  AdvertisementTableProps
>(({ modalRef, onOrderChange }, ref) => {
  const { t } = useTranslation();
  const deleteModalRef = useRef<DeleteAdvertisementModalRef>(null);
  const [selectedAdId, setSelectedAdId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState<boolean>(false);

  // Use the advertisements store
  const {
    advertisements,
    loading,
    fetchAdvertisements,
    toggleAdvertisementStatus,
    reorderAdvertisements,
  } = useAdvertisementsStore();

  // Use the alert store
  const { showError, showSuccess } = useAlertStore();

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch advertisements on component mount
  useEffect(() => {
    fetchAdvertisements();
  }, [fetchAdvertisements]);

  // Notify parent when order state changes
  useEffect(() => {
    if (onOrderChange) {
      onOrderChange(!!pendingOrder, isSavingOrder);
    }
  }, [pendingOrder, isSavingOrder, onOrderChange]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    saveOrder: handleSaveOrder,
    cancelReorder: handleCancelReorder,
    hasPendingOrder: () => !!pendingOrder,
    isSaving: () => isSavingOrder,
  }));

  const handleStatusToggle = async (id: string) => {
    setTogglingId(id);
    try {
      const updatedAdvertisement = await toggleAdvertisementStatus(id);
      if (updatedAdvertisement) {
        const messageKey =
          updatedAdvertisement.status === "ACTIVE"
            ? "advertisement_now_active"
            : "advertisement_now_inactive";
        showSuccess(t(messageKey, { id: updatedAdvertisement.id }));
      }
    } catch (err) {
      const errorMessage = (err as IApiResponse)?.error?.message;
      if (
        errorMessage &&
        errorMessage.toLowerCase().includes("count of active")
      ) {
        showError(t("advertisement_image_count_error"));
      } else {
        showError(
          t("advertisement_status_update_error"),
          t("advertisement_status_update_error_message")
        );
      }
    } finally {
      setTogglingId(null);
    }
  };

  const handleEdit = (id: string) => {
    modalRef.current?.openModal(id);
  };

  const handleDelete = (id: string) => {
    setSelectedAdId(id);
    deleteModalRef.current?.openModal();
  };

  const handleDeleteSuccess = () => {
    setSelectedAdId(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Use current display order (either pending or original) for calculating new positions
      const currentOrder = pendingOrder || advertisements.map((ad) => ad.id);

      const oldIndex = currentOrder.findIndex((id) => id === active.id);
      const newIndex = currentOrder.findIndex((id) => id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrderIds = arrayMove(currentOrder, oldIndex, newIndex);

        // Store pending order to show Save Order button
        setPendingOrder(newOrderIds);
      }
    }
  };

  const handleSaveOrder = async () => {
    if (!pendingOrder) return;

    setIsSavingOrder(true);
    try {
      await reorderAdvertisements(pendingOrder);
      showSuccess(t("advertisement_reorder_success"));
      setPendingOrder(null);
      await fetchAdvertisements();
    } catch (err) {
      // Check if it's a 409 conflict error
      const error = err as { response?: { status?: number }; status?: number };
      if (error?.response?.status === 409 || error?.status === 409) {
        showError(t("advertisement_reorder_conflict"));
      } else {
        showError(t("advertisement_reorder_error"));
      }
      // Fetch fresh data on error to reset the order
      await fetchAdvertisements();
      setPendingOrder(null);
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleCancelReorder = () => {
    setPendingOrder(null);
    fetchAdvertisements(); // Reset to original order
  };

  const columns = [
    {
      key: "webImage",
      title: `${t("advertisement_image")}`,
      width: "30%",
      align: "right" as const,
      render: (_, record: IAdvertisement) => (
        <img
          src={record.webImage ?? record.mobileImage}
          alt="Advertisement banner"
          className={styles.banner_image}
        />
      ),
    },
    {
      key: "surfaces",
      title: `${t("advertisement_platforms")}`,
      width: "20%",
      align: "right" as const,
      render: (_, record: IAdvertisement) => (
        <div className={styles.platforms_container}>
          {record.surfaces.map((platform, index) => {
            const formattedPlatform =
              platform === "IOS"
                ? "iOS"
                : platform.charAt(0).toUpperCase() +
                  platform.slice(1).toLowerCase();

            return (
              <span key={index} className={styles.platform_tag}>
                {formattedPlatform}
              </span>
            );
          })}
        </div>
      ),
    },
    {
      key: "status",
      title: `${t("advertisement_status")}`,
      width: "10%",
      align: "right" as const,
      render: (_, record: IAdvertisement) => {
        const isActive = record.status === "ACTIVE";
        const isToggling = togglingId === record.id;

        return (
          <div className={styles.status_container}>
            <SwitchText
              value={isActive}
              disabled={loading || isToggling}
              onChange={() => handleStatusToggle(record.id)}
            />
          </div>
        );
      },
    },
    {
      key: "updatedAt",
      title: `${t("advertisement_last_updated")}`,
      width: "20%",
      align: "right" as const,
      render: (_, record: IAdvertisement) => (
        <div className={styles.updated_at_container}>
          <p className={styles.updated_at_text}>{getDate(record.updatedAt)}</p>
        </div>
      ),
    },
    {
      key: "actions",
      title: `${t("procedures")}`,
      width: "15%",
      render: (_, record: IAdvertisement) => (
        <div className={styles.actions_container}>
          <p
            className={styles.edit_button}
            onClick={() => handleEdit(record.id)}>
            {t("edit")}
          </p>
          <p
            className={styles.delete_button}
            onClick={() => handleDelete(record.id)}>
            {t("delete")}
          </p>
        </div>
      ),
    },
  ];

  // Shimmer loading component
  const ShimmerRows = () => (
    <div className={styles.shimmer_container}>
      {[...Array(5)].map((_, index) => (
        <div key={index} className={styles.shimmer_row}>
          <div className={styles.shimmer_cell} style={{ width: "10%" }}>
            <div className={styles.shimmer_content} />
          </div>
          {columns.map((column) => (
            <div
              key={column.key}
              className={styles.shimmer_cell}
              style={{ width: column.width }}>
              <div className={styles.shimmer_content} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className={styles.empty_state}>
      <img
        src={EmptyIcon}
        alt="No advertisements"
        className={styles.empty_icon}
      />
      <h3 className={styles.empty_title}>{t("advertisement_no_data_title")}</h3>
      <p className={styles.empty_description}>
        {t("advertisement_no_data_description")}
      </p>
      <button
        className={styles.add_advertisement_button}
        onClick={() => modalRef.current?.openModal()}>
        {t("advertisement_add_button")}
      </button>
    </div>
  );

  // Get the display order - use pending order if exists, otherwise use advertisements
  const displayAdvertisements = pendingOrder
    ? pendingOrder
        .map((id) => advertisements.find((ad) => ad.id === id))
        .filter((ad): ad is IAdvertisement => ad !== undefined)
    : advertisements;

  return (
    <>
      <div className={`${styles.table} ${styles.alerts_table}`}>
        {/* Header */}
        <div className={styles.table_header}>
          <div
            className={styles.header_cell}
            style={{ width: "10%", textAlign: "right" }}>
            {t("advertisement_order")}
          </div>
          {columns.map((column) => (
            <div
              key={column.key}
              className={styles.header_cell}
              style={{
                width: column.width,
                textAlign: column.align || "right",
              }}>
              {column.title}
            </div>
          ))}
        </div>

        {/* Body with Drag and Drop */}
        {loading ? (
          <ShimmerRows />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}>
            <SortableContext
              items={displayAdvertisements.map((ad) => ad.id)}
              strategy={verticalListSortingStrategy}>
              <div className={styles.table_body}>
                {displayAdvertisements.map((record) => (
                  <SortableRow key={record.id} advertisement={record}>
                    {columns.map((column) => (
                      <div
                        key={column.key}
                        className={styles.table_cell}
                        style={{
                          width: column.width,
                          textAlign: column.align || "right",
                        }}>
                        {column.render
                          ? column.render(record[column.key], record)
                          : String(record[column.key] ?? "")}
                      </div>
                    ))}
                  </SortableRow>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {!loading && advertisements.length === 0 && <EmptyState />}
      <DeleteAdvertisementModal
        ref={deleteModalRef}
        advertisementId={selectedAdId}
        onSuccess={handleDeleteSuccess}
      />
    </>
  );
});

AdvertisementTable.displayName = "AdvertisementTable";

export default AdvertisementTable;
